// Supply Network panel: unified "Dealer locations & plant assignment"
// SectionCard hosting both the App-using subscriber locations
// (public.locations + mfg_subscriber_location_settings overlay) and
// the Non-app dealer locations (mfg_dealer_locations) under a single
// tab/slider, with a shared search input pinned at the top.
//
// Mode-switch UX mirrors the Inventory panel's
// Materials/Recipes/Changelog/Receipts slider via the
// ManufacturingTabbedSection primitive: one rounded box, segmented
// control across the top, body swaps below. Search is the only
// control hoisted into this wrapper — per-tab filters live in each
// body because their shapes diverge (linked-org vs subscriber-org).
//
// The wrapper owns both hooks so the data is pre-loaded and switching
// tabs is instant. Two parallel reads against /api/mfg/dealers (one
// per hook) are accepted as the cost of slice 5; a future slice can
// lift the dealers fetch up if it becomes a hot path.
import { useEffect, useMemo, useState } from "react";
import { SectionCard } from "@/components/common/SectionCard";
import type { MfgPlant } from "@/lib/manufacturingClient";
import { defaultPlantOf } from "./dealerLocationPlantResolver";
import { ManufacturingPlantNonAppLocationsBody } from "./ManufacturingPlantNonAppLocationsBody";
import { ManufacturingPlantSubscriberLocationsBody } from "./ManufacturingPlantSubscriberLocationsBody";
import { ManufacturingTabbedSection, type TabbedSectionTab } from "./ManufacturingTabbedSection";
import { useManufacturingPlantDealerLocations } from "./useManufacturingPlantDealerLocations";
import { useManufacturingSubscriberLocations } from "./useManufacturingSubscriberLocations";
import { ghostBtnClass, inputClass } from "@/components/manufacturing/manufacturingSurfaceClasses";

type DealerLocationsTab = "app_using" | "non_app";

function DefaultPlantBanner({ defaultPlant }: { defaultPlant: MfgPlant | null }) {
  if (defaultPlant) {
    return (
      <div className="rounded-lg border border-indigo-100 bg-indigo-50/60 px-3 py-2 text-xs text-indigo-900 dark:border-indigo-900/50 dark:bg-indigo-950/30 dark:text-indigo-100">
        <span className="font-semibold">Your default plant:</span>{" "}
        <code>{defaultPlant.plant_code}</code> · {defaultPlant.name}
        <span className="ml-1 text-indigo-700/80 dark:text-indigo-300/80">
          — locations with no direct override (and no dealer-org default) fall back here.
        </span>
      </div>
    );
  }
  return (
    <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:border-amber-900/40 dark:bg-amber-950/30 dark:text-amber-100">
      <span className="font-semibold">No default plant set.</span> Pick a plant in
      the list above and click <em>Set default</em> to give unassigned dealer
      locations a fallback.
    </div>
  );
}

export function ManufacturingPlantDealerLocationsTabbedSection({
  plants,
  refreshSignal,
}: {
  plants: MfgPlant[];
  refreshSignal: number;
}) {
  const [activeTab, setActiveTab] = useState<DealerLocationsTab>("app_using");
  const [search, setSearch] = useState("");

  const appUsing = useManufacturingSubscriberLocations(plants, refreshSignal);
  const nonApp = useManufacturingPlantDealerLocations(plants, refreshSignal);

  // The wrapper owns the search input visually; each hook keeps its
  // own internal `search` state for back-compat, and we mirror the
  // wrapper-level value down into both hooks on every keystroke so the
  // filter pipelines see it. Switching tabs preserves the query.
  useEffect(() => {
    appUsing.setSearch(search);
    nonApp.setSearch(search);
  }, [search, appUsing.setSearch, nonApp.setSearch]);

  const defaultPlant = useMemo(() => defaultPlantOf(plants), [plants]);

  const tabs: ReadonlyArray<TabbedSectionTab<DealerLocationsTab>> = useMemo(() => {
    const appCount = appUsing.subscriberLocations.length;
    const nonAppCount = nonApp.dealerLocations.length;
    return [
      {
        id: "app_using",
        label: appCount > 0 ? `App-using (${appCount})` : "App-using",
      },
      {
        id: "non_app",
        label: nonAppCount > 0 ? `Non-app (${nonAppCount})` : "Non-app",
      },
    ];
  }, [appUsing.subscriberLocations.length, nonApp.dealerLocations.length]);

  const listBusy = appUsing.listBusy || nonApp.listBusy;
  const actionBusy = appUsing.actionBusy || nonApp.actionBusy;

  // Slice 10: the search input + tab strip both live INLINE inside
  // the outer SectionCard now — no bordered search wrapper and no
  // inner ManufacturingTabbedSection card chrome (the inner tabbed
  // section is rendered in `bare` mode, just the tab strip + body).
  // That removes two layers of `p-4 + rounded-2xl + shadow` so the
  // search field sits next to the tab strip with normal SectionCard
  // padding around them, not three nested boxes.
  return (
    <SectionCard
      title="Dealer locations"
      description="Pick a plant per row for a quick reassign, or check multiple rows and use the sticky bar to bulk-assign. Each row's effective plant resolves direct → dealer-org default → your default plant."
      rightSlot={
        <button
          type="button"
          className={ghostBtnClass}
          onClick={() => {
            void appUsing.reload();
            void nonApp.reload();
          }}
          disabled={listBusy || actionBusy}
        >
          {listBusy ? "Refreshing…" : "Refresh"}
        </button>
      }
    >
      <DefaultPlantBanner defaultPlant={defaultPlant} />

      <label className="block text-xs text-gray-600 dark:text-zinc-300">
        <span className="sr-only">Search dealer locations</span>
        <input
          type="search"
          className={inputClass}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search dealer locations — name, city, postal, subscriber, alias, or linked org…"
        />
      </label>

      <ManufacturingTabbedSection<DealerLocationsTab>
        bare
        tabs={tabs}
        activeTab={activeTab}
        onTabChange={setActiveTab}
      >
        {activeTab === "app_using" ? (
          <ManufacturingPlantSubscriberLocationsBody m={appUsing} />
        ) : (
          <ManufacturingPlantNonAppLocationsBody m={nonApp} />
        )}
      </ManufacturingTabbedSection>
    </SectionCard>
  );
}
